import { db, functions } from './firebase';
import { doc, getDoc, setDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { ClinicalPearl } from '../types';

export { type ClinicalPearl } from '../types';

export const getTodayPearl = async (): Promise<ClinicalPearl | null> => {
    try {
        const docRef = doc(db, 'clinical_pearls', 'today');
        const snap = await getDoc(docRef);
        if (snap.exists()) {
            return snap.data() as ClinicalPearl;
        }
        return null;
    } catch (error) {
        console.error("Error fetching today's pearl:", error);
        return null;
    }
};

export const getDraftPearl = async (): Promise<ClinicalPearl | null> => {
    try {
        const docRef = doc(db, 'clinical_pearls', 'draft');
        const snap = await getDoc(docRef);
        if (snap.exists()) {
            return snap.data() as ClinicalPearl;
        }
        return null;
    } catch (error) {
        return null;
    }
};

export const getUpcomingPearls = async (): Promise<ClinicalPearl[]> => {
    try {
        const today = new Date();
        const nextWeek = new Date();
        nextWeek.setDate(today.getDate() + 8);
        
        const todayStr = today.toISOString().split('T')[0];
        const nextWeekStr = nextWeek.toISOString().split('T')[0];

        const pearlsRef = collection(db, 'clinical_pearls');
        const q = query(
            pearlsRef, 
            where('scheduledDate', '>=', todayStr),
            where('scheduledDate', '<=', nextWeekStr)
        );
        
        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => ({ ...d.data(), id: d.id } as ClinicalPearl));
    } catch (e) {
        console.error("Error fetching upcoming schedule:", e);
        return [];
    }
};

export const forceGenerateDraft = async (topic?: string): Promise<ClinicalPearl> => {
    const fn = httpsCallable(functions, 'forcePearlDraft');
    const result = await fn({ topic });
    return (result.data as any).pearl;
};

export const publishPearl = async (pearl: ClinicalPearl): Promise<void> => {
    const fn = httpsCallable(functions, 'publishPearl');
    await fn(pearl);
};

export async function getPearlQueue(): Promise<ClinicalPearl[]> {
    const list: ClinicalPearl[] = [];
    try {
        // Fetch specific draft
        const draftRef = doc(db, 'clinical_pearls', 'draft');
        const draftSnap = await getDoc(draftRef);
        if (draftSnap.exists()) list.push({ ...draftSnap.data(), id: 'draft', status: 'DRAFT' } as ClinicalPearl);
        
        // Fetch upcoming
        const upcoming = await getUpcomingPearls();
        // Remove duplicates if draft is in upcoming
        const upcomingFiltered = upcoming.filter(u => u.id !== 'draft');
        return [...list, ...upcomingFiltered];
    } catch (e) {
        console.error("Error fetching pearl queue", e);
    }
    return list;
}

export async function savePearl(pearl: ClinicalPearl): Promise<void> {
    // If it has a date, save to that date doc ID. Else save to 'draft'.
    const docId = (pearl.status === 'PUBLISHED' || pearl.scheduledDate) 
        ? (pearl.scheduledDate ? pearl.scheduledDate.split('T')[0] : 'draft') 
        : 'draft';
        
    const docRef = doc(db, 'clinical_pearls', docId);
    await setDoc(docRef, pearl);
}

export async function getEditorialConfig(): Promise<{ autoMode: boolean }> {
    const docRef = doc(db, 'system_config', 'editorial');
    const snap = await getDoc(docRef);
    if (snap.exists()) {
        return snap.data() as { autoMode: boolean };
    }
    return { autoMode: false };
}

export async function setEditorialConfig(autoMode: boolean): Promise<void> {
    const docRef = doc(db, 'system_config', 'editorial');
    await setDoc(docRef, { autoMode }, { merge: true });
}